import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { useVideosData } from "@/hooks/useVideosData";
import { Play, ArrowRight } from "lucide-react";

export const VideosCourseDetailPage: React.FC = () => {
  const { videos, collections, loading, error, refetch } = useVideosData();
  const pathSegments = window.location.pathname.split("/");
  const slug = pathSegments[3] || "";

  const collection = collections.find((c) => c.slug === slug);

  if (loading) {
    return (
      <VideosLayout loading={true}>
        <div />
      </VideosLayout>
    );
  }

  if (!collection) {
    return (
      <VideosLayout error={error} onRetry={refetch}>
        <SEOHead
          title="Course Not Found | JNN Homes Northern Virginia"
          description="The requested video course could not be found."
          noIndex={true}
        />
        <div className="p-12 text-center rounded-2xl bg-slate-50 border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Course not found
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            We couldn't find that course.
          </p>
          <Link
            to="/videos/playlists"
            className="text-sm font-semibold text-[#9A5207] hover:underline"
          >
            Browse all playlists
          </Link>
        </div>
      </VideosLayout>
    );
  }

  // Find videos for this collection in order
  const courseVideos = collection.lessons
    .map((id) => videos.find((v) => v.id === id))
    .filter((v): v is NonNullable<typeof v> => Boolean(v));

  const firstVideoId = courseVideos[0]?.id;
  const canonicalUrl = `https://www.jnnhomes.com/videos/course/${slug}`;

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title={`${collection.title} | JNN Homes Northern Virginia`}
        description={collection.description}
        canonical={canonicalUrl}
      />

      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium flex-wrap">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <Link to="/videos/playlists" className="hover:text-[#9A5207]">
            Playlists
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">
            {collection.title}
          </span>
        </nav>

        {/* Cover / Header */}
        <div className="space-y-4">
          <div className="relative aspect-[16/9] sm:aspect-[21/9] rounded-2xl overflow-hidden bg-slate-200 shadow-sm">
            <img
              src={`https://i.ytimg.com/vi/${firstVideoId}/hqdefault.jpg`}
              alt={collection.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
              <Link
                to={`/videos/course/${slug}/${firstVideoId}`}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E8850F] text-white font-bold text-sm hover:bg-[#B35A06] transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              >
                <Play className="w-5 h-5 fill-white" /> Start watching
              </Link>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 text-[10px] font-bold uppercase rounded bg-[#E8850F]/14 text-[#9A5207]">
                {collection.category}
              </span>
              <span className="text-xs font-semibold text-slate-500">
                {collection.lessonCount} lessons
              </span>
            </div>
            <h1 className="text-2xl font-extrabold text-slate-900">
              {collection.title}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {collection.description}
            </p>
          </div>
        </div>

        {/* Numbered Lesson List */}
        <section className="space-y-3">
          <h2 className="text-base font-bold text-slate-900">
            Lessons in this course
          </h2>
          <ol className="space-y-2">
            {courseVideos.map((video, idx) => (
              <li key={video.id}>
                <Link
                  to={`/videos/course/${slug}/${video.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#E8850F] hover:bg-slate-50/50 transition-all group focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                >
                  <span className="w-8 h-8 shrink-0 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-sm font-bold group-hover:bg-[#E8850F]/14 group-hover:text-[#9A5207] transition-colors">
                    {idx + 1}
                  </span>
                  <div className="w-16 h-9 rounded-md overflow-hidden bg-slate-100 shrink-0">
                    <img
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-[#9A5207] transition-colors truncate">
                      {video.title}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {video.duration} · {video.category}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#9A5207] group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </VideosLayout>
  );
};
